import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Eye, EyeOff, ArrowLeft, Save, X, MinusCircle, PlusCircle } from 'lucide-react';
import type { Database } from '@/lib/database.types';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type Product = Database['public']['Tables']['products']['Row'];
type Producer = Database['public']['Tables']['producers']['Row'];

export function ProducerProducts() {
  const { producerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [producer, setProducer] = React.useState<Producer | null>(null);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [editingStock, setEditingStock] = React.useState<Record<string, { editing: boolean, value: number }>>({});

  React.useEffect(() => {
    if (!producerId) return;

    async function fetchData() {
      try {
        setLoading(true);
        
        // Fetch producer info
        const { data: producerData, error: producerError } = await supabase
          .from('producers')
          .select('*')
          .eq('id', producerId)
          .single();

        if (producerError) throw producerError;
        setProducer(producerData);

        // Fetch products for this producer
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .eq('producer_id', producerId)
          .order('created_at', { ascending: false });

        if (productsError) throw productsError;
        setProducts(productsData || []);
        
        // Initialize stock editing state
        const initialEditingStock: Record<string, { editing: boolean, value: number }> = {};
        productsData?.forEach(product => {
          initialEditingStock[product.id] = { 
            editing: false, 
            value: product.stock_level 
          };
        });
        setEditingStock(initialEditingStock);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [producerId]);

  async function togglePublished(product: Product) {
    try {
      const { error } = await supabase
        .from('products')
        .update({ is_published: !product.is_published })
        .eq('id', product.id);

      if (error) throw error;

      setProducts(products.map(p =>
        p.id === product.id ? { ...p, is_published: !p.is_published } : p
      ));
    } catch (error) {
      console.error('Error toggling product visibility:', error);
    }
  }
  
  function startEditingStock(productId: string, currentValue: number) {
    setEditingStock({
      ...editingStock,
      [productId]: { editing: true, value: currentValue }
    });
  }
  
  function cancelEditingStock(productId: string, originalValue: number) {
    setEditingStock({
      ...editingStock,
      [productId]: { editing: false, value: originalValue }
    });
  }
  
  function updateStockValue(productId: string, change: number) {
    const currentState = editingStock[productId];
    if (!currentState) return;
    
    const newValue = Math.max(0, currentState.value + change);
    setEditingStock({
      ...editingStock,
      [productId]: { ...currentState, value: newValue }
    });
  }
  
  async function saveStockLevel(productId: string) {
    try {
      const newStockLevel = editingStock[productId]?.value;
      if (newStockLevel === undefined) return;
      
      const { error } = await supabase
        .from('products')
        .update({ stock_level: newStockLevel })
        .eq('id', productId);

      if (error) throw error;
      
      // Update local products state
      setProducts(products.map(p => 
        p.id === productId ? { ...p, stock_level: newStockLevel } : p
      ));
      
      // Exit editing mode
      setEditingStock({
        ...editingStock,
        [productId]: { editing: false, value: newStockLevel }
      });
      
      toast.success(t('products.stockLevelUpdated', 'Lagernivå uppdaterad'));
    } catch (error) {
      console.error('Error updating stock level:', error);
      toast.error(t('products.stockUpdateFailed', 'Kunde inte uppdatera lagernivån'));
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <button 
          onClick={() => navigate(`/admin/producers/${producerId}/edit`)} 
          className="text-gray-500 hover:text-gray-700 mr-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('producers.productsFor', 'Produkter för')}: {producer?.company_name}
        </h1>
        <div className="ml-auto">
          <Link
            to={`/admin/producers/${producerId}/products/new`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('products.addProduct', 'Lägg till produkt')}
          </Link>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              {products.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="mt-2 text-sm font-medium text-gray-900">{t('products.noProductsFound', 'Inga produkter hittades')}</h3>
                  <p className="mt-1 text-sm text-gray-500">{t('products.startByCreating', 'Börja med att skapa en ny produkt.')}</p>
                  <div className="mt-6">
                    <Link
                      to={`/admin/producers/${producerId}/products/new`}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                      {t('products.newProduct', 'Ny produkt')}
                    </Link>
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {t('products.product', 'Produkt')}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {t('products.price', 'Pris')}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {t('products.stock', 'Lager')}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {t('products.status', 'Status')}
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">{t('common.actions', 'Åtgärder')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.map((product) => (
                      <tr key={product.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={product.image_urls[0] || 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80'}
                                alt=""
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500">{product.description?.slice(0, 50)}{product.description && product.description.length > 50 ? '...' : ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Intl.NumberFormat('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                            minimumFractionDigits: 2
                          }).format(product.price)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          {editingStock[product.id]?.editing ? (
                            <div className="flex items-center space-x-1">
                              <button 
                                className="text-gray-400 hover:text-gray-600"
                                onClick={() => updateStockValue(product.id, -1)}
                                aria-label={t('products.decreaseStock', 'Minska lager')}
                              >
                                <MinusCircle className="h-4 w-4" />
                              </button>
                              <input
                                type="number"
                                className="w-14 text-center text-sm border border-gray-300 rounded-md p-1"
                                value={editingStock[product.id]?.value}
                                min="0"
                                aria-label={t('products.stockQuantity', 'Lagerantal')}
                                onChange={(e) => setEditingStock({
                                  ...editingStock,
                                  [product.id]: { 
                                    ...editingStock[product.id], 
                                    value: Math.max(0, parseInt(e.target.value) || 0) 
                                  }
                                })}
                              />
                              <button 
                                className="text-gray-400 hover:text-gray-600"
                                onClick={() => updateStockValue(product.id, 1)}
                                aria-label={t('products.increaseStock', 'Öka lager')}
                              >
                                <PlusCircle className="h-4 w-4" />
                              </button>
                              <button
                                className="ml-1 text-green-500 hover:text-green-600"
                                onClick={() => saveStockLevel(product.id)}
                                title={t('common.save', 'Spara')}
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                className="text-red-500 hover:text-red-600"
                                onClick={() => cancelEditingStock(product.id, product.stock_level)}
                                title={t('common.cancel', 'Avbryt')}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="text-sm text-gray-500 cursor-pointer hover:text-blue-500 hover:underline"
                              onClick={() => startEditingStock(product.id, product.stock_level)}
                              title={t('products.clickToEditStock', 'Klicka för att ändra lagernivå')}
                            >
                              {product.stock_level}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.is_published
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {product.is_published ? t('products.published', 'Publicerad') : t('products.draft', 'Utkast')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button
                              onClick={() => togglePublished(product)}
                              className="text-gray-400 hover:text-gray-500"
                              title={product.is_published ? t('products.unpublish', 'Avpublicera') : t('products.publish', 'Publicera')}
                            >
                              {product.is_published ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                            <Link
                              to={`/admin/producers/${producerId}/products/${product.id}/edit`}
                              className="text-indigo-600 hover:text-indigo-900"
                              title={t('common.edit', 'Redigera')}
                            >
                              <Edit className="h-5 w-5" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 